import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Download } from "lucide-react";
import type { ExportOptions } from "@/services/mapExportImport";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mapName: string;
  counts: {
    seeds: number;
    nodes: number;
    entities: number;
    entity_attributes: number;
    relations: number;
    writing_profiles: number;
    node_content: number;
    knowledge_graphs: number;
    serp_cache: number;
  };
  onConfirm: (opts: ExportOptions) => void | Promise<void>;
}

interface Section {
  key: keyof ExportOptions;
  label: string;
  description: string;
  countKey:
    | "seeds"
    | "nodes"
    | "entities"
    | "entity_attributes"
    | "relations"
    | "writing_profiles"
    | "node_content"
    | "knowledge_graphs"
    | "serp_cache";
}

const SECTIONS: Section[] = [
  {
    key: "includeSeeds",
    label: "Seedy (klastry)",
    description: "thematic_seeds",
    countKey: "seeds",
  },
  {
    key: "includeNodes",
    label: "Nody (artykuły)",
    description: "thematic_nodes — wyłączenie KASKADOWO wyłącza relacje/CE/KG/SERP (bo mają FK na node_id)",
    countKey: "nodes",
  },
  {
    key: "includeEntities",
    label: "Encje",
    description: "thematic_entities",
    countKey: "entities",
  },
  {
    key: "includeEav",
    label: "EAV (atrybuty encji)",
    description: "thematic_entity_attributes — wymaga encji",
    countKey: "entity_attributes",
  },
  {
    key: "includeRelations",
    label: "Relacje między nodami",
    description: "thematic_node_relations — wymaga nodów",
    countKey: "relations",
  },
  {
    key: "includeWritingProfiles",
    label: "Profile pisarskie",
    description: "thematic_writing_profiles",
    countKey: "writing_profiles",
  },
  {
    key: "includeNodeContent",
    label: "Content Engine (outline + HTML)",
    description: "thematic_node_content — duży, wymaga nodów",
    countKey: "node_content",
  },
  {
    key: "includeKnowledgeGraphs",
    label: "Knowledge Graphs",
    description: "content_knowledge_graphs — wymaga nodów",
    countKey: "knowledge_graphs",
  },
  {
    key: "includeSerpCache",
    label: "Cache SERP",
    description: "content_serp_cache (30-dniowy) — wymaga nodów",
    countKey: "serp_cache",
  },
];

export function ExportOptionsDialog({
  open,
  onOpenChange,
  mapName,
  counts,
  onConfirm,
}: Props) {
  const [opts, setOpts] = useState<ExportOptions>({
    includeSeeds: true,
    includeNodes: true,
    includeEntities: true,
    includeEav: true,
    includeRelations: true,
    includeWritingProfiles: true,
    includeNodeContent: true,
    includeKnowledgeGraphs: true,
    includeSerpCache: true,
  });
  const [exporting, setExporting] = useState(false);

  const toggle = (key: keyof ExportOptions) => {
    setOpts((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setAll = (value: boolean) => {
    setOpts({
      includeSeeds: value,
      includeNodes: value,
      includeEntities: value,
      includeEav: value,
      includeRelations: value,
      includeWritingProfiles: value,
      includeNodeContent: value,
      includeKnowledgeGraphs: value,
      includeSerpCache: value,
    });
  };

  const handleConfirm = async () => {
    setExporting(true);
    try {
      await onConfirm(opts);
      onOpenChange(false);
    } finally {
      setExporting(false);
    }
  };

  // Zaleznosci (dependency check):
  //   - EAV wymaga Entities (bez encji nie ma do czego przypisac atrybutow)
  //   - Relations / NodeContent / KG / SERP wymagaja Nodes (FK na node_id)
  const isDisabledByParent = (key: keyof ExportOptions): boolean => {
    if (key === "includeEav" && !opts.includeEntities) return true;
    if (
      (key === "includeRelations" ||
        key === "includeNodeContent" ||
        key === "includeKnowledgeGraphs" ||
        key === "includeSerpCache") &&
      !opts.includeNodes
    ) {
      return true;
    }
    return false;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Eksport mapy: {mapName}</DialogTitle>
          <DialogDescription>
            Wybierz co dołączyć. <strong>Macro</strong> (scope, SBD, audience) jest zawsze eksportowany — reszta opcjonalnie.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Always-on info */}
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-muted-foreground">
            <div className="font-medium text-foreground mb-1">Zawsze w eksporcie:</div>
            <div>
              Macro (thematic_maps row — macro_statement, scope_in/out, audience, SBD)
            </div>
          </div>

          {/* Buttons all/none */}
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setAll(true)}
              className="flex-1"
            >
              Zaznacz wszystkie
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setAll(false)}
              className="flex-1"
            >
              Odznacz wszystkie
            </Button>
          </div>

          {/* Sections */}
          <div className="space-y-2 rounded-lg border p-3 max-h-[50vh] overflow-y-auto">
            {SECTIONS.map((s) => {
              const isChecked = opts[s.key] ?? true;
              const count = counts[s.countKey];
              const disabled = isDisabledByParent(s.key);
              const isNodesWithCascade = s.key === "includeNodes" && !opts.includeNodes;
              return (
                <div
                  key={s.key}
                  className={`flex items-start gap-2 ${disabled ? "opacity-50" : ""}`}
                >
                  <Checkbox
                    id={`exp-${s.key}`}
                    checked={isChecked && !disabled}
                    onCheckedChange={() => !disabled && toggle(s.key)}
                    disabled={disabled}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <Label
                      htmlFor={`exp-${s.key}`}
                      className="text-sm font-medium cursor-pointer flex items-center gap-2"
                    >
                      {s.label}
                      <span className="text-xs text-muted-foreground font-normal">
                        ({count})
                      </span>
                    </Label>
                    <p className="text-xs text-muted-foreground">{s.description}</p>
                    {isNodesWithCascade && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        ⚠ Wyłączenie nodów wyłączy automatycznie: relacje, Content Engine, Knowledge Graphs, SERP cache
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={exporting}>
            Anuluj
          </Button>
          <Button onClick={handleConfirm} disabled={exporting}>
            <Download className="mr-1 h-4 w-4" />
            {exporting ? "Eksportowanie..." : "Eksportuj JSON"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
